import styled, { keyframes } from "styled-components";

type StatusContrato = "PENDENTE" | "ENVIADO" | "APROVADO" | "REJEITADO";

/* Paleta da plataforma: azul #024995 / #009bd9, laranja #f39200, bordas #e8eef5. */
export const CORES_STATUS: Record<
  StatusContrato,
  { fundo: string; borda: string; texto: string; ponto: string }
> = {
  PENDENTE: { fundo: "#fff8ec", borda: "#ffd8a8", texto: "#a35b00", ponto: "#f39200" },
  ENVIADO: { fundo: "#eef6fd", borda: "#c5e0f7", texto: "#0b5a92", ponto: "#009bd9" },
  APROVADO: { fundo: "#eafaf1", borda: "#b7e4c7", texto: "#1b7a4b", ponto: "#22a35f" },
  REJEITADO: { fundo: "#fdecec", borda: "#f5c2c2", texto: "#a12727", ponto: "#dc3545" },
};

const surgir = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const girar = keyframes`
  to { transform: rotate(360deg); }
`;

/* Fundo cinza + cards brancos: mesmo enquadramento da tela de Usuários. */
export const Pagina = styled.div`
  background: #f5f5f5;
  min-height: 100vh;
  padding: 22px 24px 48px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #1a2740;
`;

const cartao = `
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(16, 44, 76, 0.08), 0 6px 18px rgba(16, 44, 76, 0.04);
`;

export const Cabecalho = styled.header`
  ${cartao}
  padding: 22px 26px;
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
  animation: ${surgir} 0.35s ease-out both;
`;

export const Identidade = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  h1 {
    font-size: 22px;
    font-weight: 800;
    color: #1a2740;
    margin: 0 0 3px;
    letter-spacing: -0.2px;
  }

  p {
    margin: 0;
    font-size: 13.5px;
    color: #7a8fa6;
    max-width: 560px;
    line-height: 1.5;
  }

  strong {
    color: #024995;
    font-weight: 700;
  }
`;

/* Quadrado com gradiente da marca, como âncora visual do título. */
export const IconeTitulo = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 14px;
  background: linear-gradient(135deg, #024995 0%, #009bd9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 6px 16px rgba(2, 73, 149, 0.28);

  i {
    color: #fff;
    font-size: 21px;
  }
`;

export const Contadores = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

export const Contador = styled.button<{ $ativo: boolean; $cor: string; $fundo: string; $borda: string }>`
  position: relative;
  background: ${(p) => (p.$ativo ? p.$fundo : "#fff")};
  border: 1.5px solid ${(p) => (p.$ativo ? p.$borda : "#e8eef5")};
  border-radius: 12px;
  padding: 9px 16px 9px 14px;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  min-width: 100px;
  transition: all 0.18s ease;
  overflow: hidden;

  /* Faixa colorida à esquerda identifica o status sem depender só do texto. */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${(p) => p.$cor};
    opacity: ${(p) => (p.$ativo ? 1 : 0.35)};
    transition: opacity 0.18s ease;
  }

  &:hover {
    border-color: ${(p) => p.$borda};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 44, 76, 0.08);

    &::before {
      opacity: 1;
    }
  }

  strong {
    display: block;
    font-size: 21px;
    font-weight: 800;
    color: ${(p) => p.$cor};
    line-height: 1.15;
  }

  span {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #8698ab;
  }
`;

export const Filtros = styled.section`
  ${cartao}
  padding: 18px 22px;
  margin-bottom: 18px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
  align-items: end;
  animation: ${surgir} 0.35s ease-out 0.04s both;
`;

export const Campo = styled.div`
  min-width: 0;

  label {
    display: block;
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.9px;
    text-transform: uppercase;
    color: #8698ab;
    margin-bottom: 6px;
  }

  .entrada {
    position: relative;

    i {
      position: absolute;
      left: 13px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 13px;
      color: #b3c1d1;
      pointer-events: none;
    }
  }

  input,
  select {
    width: 100%;
    border: 1.5px solid #e8eef5;
    border-radius: 10px;
    padding: 10px 12px 10px 34px;
    font-size: 13.5px;
    background: #f7fafd;
    color: #1a2740;
    outline: none;
    box-sizing: border-box;
    font-family: inherit;
    transition: all 0.18s ease;
    height: 41px;

    &::placeholder {
      color: #b3c1d1;
    }

    &:focus {
      border-color: #009bd9;
      background: #fff;
      box-shadow: 0 0 0 3px rgba(0, 155, 217, 0.12);
    }
  }

  select {
    padding-left: 34px;
    appearance: none;
    cursor: pointer;
  }
`;

export const BotaoLimpar = styled.button`
  border: 1.5px solid #e8eef5;
  background: #fff;
  color: #7a8fa6;
  border-radius: 10px;
  height: 41px;
  padding: 0 16px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  transition: all 0.18s ease;

  &:hover {
    border-color: #f39200;
    color: #b56a00;
    background: #fff8ec;
  }
`;

/*
  Sem `overflow: hidden` para o menu de ações não ser cortado; o arredondamento das
  pontas fica por conta do cabeçalho e da última linha.
*/
export const Tabela = styled.section`
  ${cartao}
  animation: ${surgir} 0.35s ease-out 0.08s both;
  /*
    O transform que a animacao retem cria um contexto de empilhamento, prendendo o
    z-index do menu aqui dentro. Sem esta ordem explicita, a paginacao (que vem depois
    no DOM) seria pintada por cima do menu aberto.
  */
  position: relative;
  z-index: 5;
`;

/*
  Cada linha é um grid independente, então as colunas precisam ter largura resolvível
  sem depender do conteúdo — com `auto` na coluna de ações, uma linha com 4 botões
  calcularia colunas diferentes das demais e a tabela sairia de prumo.
*/
const grade = `
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1.8fr) minmax(0, 1.05fr) 132px 352px;
  gap: 18px;
  align-items: center;
`;

export const Titulos = styled.div`
  ${grade}
  padding: 13px 24px;
  background: #f7fafd;
  border-bottom: 1.5px solid #e8eef5;
  border-radius: 14px 14px 0 0;

  span {
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.9px;
    text-transform: uppercase;
    color: #8698ab;
  }

  @media (max-width: 1180px) {
    display: none;
  }
`;

export const Linha = styled.div`
  ${grade}
  padding: 16px 24px;
  border-bottom: 1px solid #f0f4f8;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
    border-radius: 0 0 14px 14px;
  }

  &:hover {
    background: #fafcfe;
  }

  /* Abaixo de 1180px a tabela vira cards empilhados. */
  @media (max-width: 1180px) {
    position: relative;
    grid-template-columns: 1fr;
    gap: 10px;
    padding: 18px 20px;
    /* Espaço reservado para o botão de ações, que fica ancorado na quina. */
    padding-right: 64px;
    border-bottom: 8px solid #f5f5f5;
  }
`;

export const Celula = styled.div`
  min-width: 0;

  .principal {
    display: block;
    font-size: 14.5px;
    font-weight: 700;
    color: #1a2740;
    overflow-wrap: anywhere;
    margin-bottom: 2px;
  }

  .apoio {
    display: block;
    font-size: 12.5px;
    color: #8698ab;
    overflow-wrap: anywhere;
    line-height: 1.5;
  }
`;

/* Selo de acesso do aluno — o dado que a coordenação confere depois de aprovar. */
export const Acesso = styled.span<{ $liberado: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 4px;
  font-size: 11.5px;
  font-weight: 700;
  color: ${(p) => (p.$liberado ? "#1b7a4b" : "#8698ab")};

  i {
    font-size: 10px;
  }
`;

/*
  div (e não span) de propósito: dentro de uma célula, um `span` herdaria
  `display: block` das regras de texto e o selo esticaria na largura toda.
*/
export const Badge = styled.div<{ $status: StatusContrato }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 13px 6px 11px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
  background: ${(p) => CORES_STATUS[p.$status]?.fundo || "#eef2f7"};
  border: 1.5px solid ${(p) => CORES_STATUS[p.$status]?.borda || "#dde5ee"};
  color: ${(p) => CORES_STATUS[p.$status]?.texto || "#7a8fa6"};
  white-space: nowrap;
  width: fit-content;

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${(p) => CORES_STATUS[p.$status]?.ponto || "#8698ab"};
    flex-shrink: 0;
  }
`;

export const Acoes = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-end;

  /* No modo card o menu deixa de ser a última linha e vai para a quina do card. */
  @media (max-width: 1180px) {
    position: absolute;
    top: 16px;
    right: 16px;
  }
`;

const botaoBase = `
  border-radius: 9px;
  padding: 8px 13px;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  border: 1.5px solid transparent;
  white-space: nowrap;
  transition: all 0.18s ease;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  line-height: 1;

  i {
    font-size: 11px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const BotaoVer = styled.a`
  ${botaoBase}
  background: #fff;
  border-color: #c5e0f7;
  color: #0b5a92;
  text-decoration: none;

  &:hover {
    background: #eef6fd;
    border-color: #009bd9;
    color: #024995;
    text-decoration: none;
  }
`;

export const BotaoAprovar = styled.button`
  ${botaoBase}
  background: linear-gradient(135deg, #1b7a4b 0%, #22a35f 100%);
  color: #fff;
  box-shadow: 0 3px 10px rgba(27, 122, 75, 0.28);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(27, 122, 75, 0.36);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

export const BotaoRejeitar = styled.button`
  ${botaoBase}
  background: #fff;
  border-color: #f5c2c2;
  color: #a12727;

  &:hover:not(:disabled) {
    background: #fdecec;
    border-color: #dc3545;
  }
`;

/* Só ícone: economiza espaço na coluna de ações, que pode ter até 4 botões. */
export const BotaoIcone = styled.button`
  ${botaoBase}
  background: none;
  border-color: #e8eef5;
  color: #8698ab;
  padding: 8px 11px;

  i {
    font-size: 13px;
  }

  &:hover:not(:disabled) {
    border-color: #009bd9;
    color: #024995;
    background: #f7fafd;
  }
`;

export const BotaoLink = styled.button`
  ${botaoBase}
  background: none;
  border-color: #e8eef5;
  color: #8698ab;

  &:hover:not(:disabled) {
    border-color: #009bd9;
    color: #024995;
    background: #f7fafd;
  }
`;

/* Botão de três pontinhos que abre o menu de ações da linha. */
export const BotaoMenu = styled.button<{ $aberto: boolean }>`
  width: 34px;
  height: 34px;
  border-radius: 9px;
  border: 1.5px solid ${(p) => (p.$aberto ? "#009bd9" : "#e8eef5")};
  background: ${(p) => (p.$aberto ? "#eef6fd" : "#fff")};
  color: ${(p) => (p.$aberto ? "#024995" : "#8698ab")};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.18s ease;
  flex-shrink: 0;

  i {
    font-size: 15px;
  }

  &:hover {
    border-color: #009bd9;
    color: #024995;
    background: #f7fafd;
  }
`;

export const Menu = styled.div<{ $paraCima: boolean }>`
  position: absolute;
  right: 0;
  ${(p) => (p.$paraCima ? "bottom: calc(100% + 6px);" : "top: calc(100% + 6px);")}
  z-index: 30;
  min-width: 212px;
  background: #fff;
  border: 1px solid #e8eef5;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(16, 44, 76, 0.16);
  padding: 6px;
  animation: ${surgir} 0.14s ease-out both;
  text-align: left;
`;

export const MenuItem = styled.button<{ $tom?: "normal" | "aprovar" | "rejeitar" }>`
  width: 100%;
  border: none;
  background: none;
  border-radius: 8px;
  padding: 10px 12px;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 11px;
  text-align: left;
  text-decoration: none;
  transition: background 0.14s ease;
  color: ${(p) =>
    p.$tom === "aprovar" ? "#1b7a4b" : p.$tom === "rejeitar" ? "#a12727" : "#33475e"};

  i {
    font-size: 13px;
    width: 15px;
    text-align: center;
    color: ${(p) =>
      p.$tom === "aprovar" ? "#22a35f" : p.$tom === "rejeitar" ? "#dc3545" : "#8698ab"};
  }

  &:hover {
    background: ${(p) =>
      p.$tom === "aprovar" ? "#eafaf1" : p.$tom === "rejeitar" ? "#fdecec" : "#f4f8fc"};
    text-decoration: none;
    color: ${(p) =>
      p.$tom === "aprovar" ? "#1b7a4b" : p.$tom === "rejeitar" ? "#a12727" : "#024995"};
  }
`;

/* Separa as ações de consulta das que mudam o status do contrato. */
export const MenuDivisor = styled.div`
  height: 1px;
  background: #eef2f7;
  margin: 5px 8px;
`;

export const Vazio = styled.div`
  padding: 56px 24px;
  text-align: center;

  i {
    font-size: 34px;
    color: #cdd9e6;
    margin-bottom: 14px;
    display: block;
  }

  strong {
    display: block;
    font-size: 16px;
    color: #1a2740;
    margin-bottom: 5px;
    font-weight: 700;
  }

  span {
    font-size: 13.5px;
    color: #8698ab;
  }
`;

export const Carregando = styled.div`
  padding: 56px 24px;
  text-align: center;
  color: #8698ab;
  font-size: 13.5px;

  .roda {
    width: 30px;
    height: 30px;
    border: 3px solid #e8eef5;
    border-top-color: #009bd9;
    border-radius: 50%;
    animation: ${girar} 0.8s linear infinite;
    margin: 0 auto 14px;
  }
`;

export const Paginacao = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 16px;
  padding: 0 4px;

  .resumo {
    font-size: 13px;
    color: #8698ab;

    strong {
      color: #024995;
      font-weight: 700;
    }
  }

  .navegacao {
    display: flex;
    gap: 8px;
  }

  button {
    border: 1.5px solid #e8eef5;
    background: #fff;
    color: #024995;
    border-radius: 9px;
    padding: 8px 15px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    transition: all 0.18s ease;

    i {
      font-size: 10px;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      border-color: #009bd9;
      background: #f7fafd;
    }
  }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(10, 30, 55, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1050;
  animation: ${surgir} 0.2s ease-out both;
`;

export const Dialogo = styled.div`
  background: #fff;
  border-radius: 18px;
  padding: 0;
  max-width: 470px;
  width: 100%;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(2, 73, 149, 0.32);
`;

export const DialogoTopo = styled.div<{ $aprovar: boolean }>`
  padding: 22px 26px;
  background: ${(p) =>
    p.$aprovar
      ? "linear-gradient(135deg, #1b7a4b 0%, #22a35f 100%)"
      : "linear-gradient(135deg, #a12727 0%, #dc3545 100%)"};
  color: #fff;
  display: flex;
  align-items: center;
  gap: 14px;

  i {
    font-size: 22px;
  }

  h2 {
    font-size: 18px;
    font-weight: 800;
    margin: 0;
  }
`;

export const DialogoCorpo = styled.div`
  padding: 22px 26px 24px;

  .resumo {
    background: #f7fafd;
    border: 1px solid #e8eef5;
    border-radius: 12px;
    padding: 14px 16px;
    margin-bottom: 16px;

    div {
      font-size: 13.5px;
      color: #4a5b6e;
      line-height: 1.7;
    }

    strong {
      color: #1a2740;
      font-weight: 700;
    }
  }

  p {
    font-size: 13.5px;
    line-height: 1.65;
    color: #5b6b7e;
    margin: 0;
  }
`;

export const CampoMotivo = styled.div<{ $erro: boolean }>`
  margin-top: 16px;

  label {
    display: block;
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.9px;
    text-transform: uppercase;
    color: #8698ab;
    margin-bottom: 7px;
  }

  textarea {
    width: 100%;
    border: 1.5px solid ${(p) => (p.$erro ? "#dc3545" : "#e8eef5")};
    border-radius: 10px;
    padding: 11px 13px;
    font-size: 13.5px;
    line-height: 1.55;
    font-family: inherit;
    background: ${(p) => (p.$erro ? "#fffafa" : "#f7fafd")};
    color: #1a2740;
    outline: none;
    box-sizing: border-box;
    resize: vertical;
    min-height: 78px;

    &::placeholder {
      color: #b3c1d1;
    }

    &:focus {
      border-color: ${(p) => (p.$erro ? "#dc3545" : "#009bd9")};
      background: #fff;
      box-shadow: 0 0 0 3px
        ${(p) => (p.$erro ? "rgba(220, 53, 69, 0.12)" : "rgba(0, 155, 217, 0.12)")};
    }

    &:disabled {
      opacity: 0.6;
    }
  }

  .rodape {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-top: 6px;
    min-height: 16px;
  }

  .erro {
    font-size: 12px;
    color: #a12727;
    font-weight: 600;
  }

  .contador {
    font-size: 11.5px;
    color: #b3c1d1;
    margin-left: auto;
    flex-shrink: 0;
  }
`;

/* Prévia do motivo na listagem — o texto completo fica no title. */
export const MotivoLinha = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.45;
  color: #a12727;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: help;
`;

export const DialogoAcoes = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 22px;
`;
